import { authoriseWithGoogle, authoriseWithXylem, getCurrentUser, resendVerifyUserCode, resetPasswordAndSignIn, sendPasswordResetCode, signIn, signOut, signUp, signUpAndInWithEmail, verifyUser } from '@/api/auth';
import hint from '@/api/rest/hint';
import { ACCOUNT_KEY } from '@/store/modules/role';
import { AUTHORISE_WITH_GOOGLE, AUTHORISE_WITH_XYLEM, FETCH_USER, HINT_USER, LOGIN_WITH_EMAIL, LOGOUT, LOGOUT_OF_ACCOUNT, REGISTER_USER_BY_DETAILS, REGISTER_USER_WITH_PASSWORD, REQUEST_PASSWORD_RESET, RESEND_VERIFICATION_EMAIL, RESET_PASSWORD, VERIFY_USER } from '@/store/types/actions';
import { SET_TEMPORARY_EMAIL, USER_FETCHED } from '@/store/types/mutations';

let tempPassword = null;

const actions = {
  [FETCH_USER]({ commit }) {
    return getCurrentUser().then((user) => {
      commit(USER_FETCHED, user);
      return Promise.resolve(user);
    });
  },
  [LOGOUT]() {
    window.localStorage.removeItem(ACCOUNT_KEY);

    return signOut().then(() => {
      window.location.assign('/'); // the laziest, but must reliable, way to flush state.
    });
  },
  [LOGOUT_OF_ACCOUNT]() {
    // Skip cognito signOut step
    window.localStorage.removeItem(ACCOUNT_KEY);
    // Use replaceState and reload to avoid flickering
    window.history.replaceState(null, null, '/#/user/accounts?force=true');
    window.location.reload();
  },
  [REGISTER_USER_BY_DETAILS]({ dispatch }, {
    firstName,
    lastName,
    email,
    phoneNumber,
    numberOfDevices,
  }) {
    if (!firstName || !lastName || !email) {
      return Promise.reject(new Error('First name, last name and email must be present'));
    }

    return signUpAndInWithEmail(email, {
      given_name: firstName,
      family_name: lastName,
    }).then(() => {
      const fields = [
        { name: 'email', value: email },
        { name: 'firstname', value: firstName },
        { name: 'lastname', value: lastName },
        { name: 'expected_number_of_devices', value: numberOfDevices },
        { name: 'invited_to_existing_account_', value: false },
      ];
      if (phoneNumber) {
        fields.push({ name: 'phone', value: phoneNumber });
      }
      return dispatch(FETCH_USER);
    });
  },
  [REGISTER_USER_WITH_PASSWORD](_, {
    firstName, lastName, email, password,
  }) {
    return signUp(email, password, {
      given_name: firstName,
      family_name: lastName,
    }).then(() => {
      tempPassword = password;
    });
  },
  [HINT_USER]({ commit, dispatch }, { email }) {
    return hint(email).then((result) => {
      if (result.exists && result.provider === 'unclaimed') {
        commit(SET_TEMPORARY_EMAIL, email);
        dispatch(REQUEST_PASSWORD_RESET, { email });
      } else if (result.exists && result.provider === 'basic') {
        commit(SET_TEMPORARY_EMAIL, email);
      }

      return Promise.resolve(result);
    });
  },
  [LOGIN_WITH_EMAIL]({ dispatch }, { email, password }) {
    return signIn(email, password).then(() => dispatch(FETCH_USER));
  },
  [AUTHORISE_WITH_GOOGLE]({ dispatch }, {
    promote = false,
    registering = false,
    email,
    phoneNumber,
    numberOfDevices,
    isInvite = false,
  } = {}) {
    return authoriseWithGoogle({ promote, email })
      .then(() => dispatch(FETCH_USER))
      .then((user) => {
        if (registering === true) {
          const fields = [
            { name: 'email', value: user.email },
            { name: 'firstname', value: user.givenName },
            { name: 'lastname', value: user.familyName },
            { name: 'expected_number_of_devices', value: numberOfDevices },
            { name: 'invited_to_existing_account_', value: isInvite },
          ];
          if (phoneNumber) {
            fields.push({ name: 'phone', value: phoneNumber });
          }
        }
      });
  },
  [AUTHORISE_WITH_XYLEM]({ dispatch }) {
    // (xylem cloud users cannot register directly)
    return authoriseWithXylem()
      .then(() => dispatch(FETCH_USER));
  },
  [REQUEST_PASSWORD_RESET](_, { email }) {
    return sendPasswordResetCode(email);
  },
  [RESET_PASSWORD]({ dispatch }, { email, code, password }) {
    return resetPasswordAndSignIn(email, code, password).then(() => dispatch(FETCH_USER));
  },
  [VERIFY_USER]({ dispatch }, { email, code }) {
    return verifyUser(email, code)
      .then(() => {
        if (tempPassword) {
          return signIn(email, tempPassword).then(() => dispatch(FETCH_USER));
        }

        return Promise.resolve();
      });
  },
  [RESEND_VERIFICATION_EMAIL](_, { email }) {
    return resendVerifyUserCode(email);
  },
};

const mutations = {
  [USER_FETCHED](state, user) {
    state.provider = user.provider;
    state.email_verified = user.email_verified;
    state.email = user.email;
    state.givenName = user.givenName;
    state.familyName = user.familyName;
    state.picture = user.picture;
    state.id = user.sub;

    // Ensure we only post to hubspot once
    state.hasIdentified = true;
  },
  [SET_TEMPORARY_EMAIL](state, email) {
    state.temporaryEmail = email;
  },
};

const state = {
  provider: null,
  verified: null,
  givenName: null,
  familyName: null,
  email: null,
  id: null,
  picture: null,
  temporaryEmail: '',
  // for hubspot tracking
  hasIdentified: false,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
